// Pure view layer for the Matches module (the per-player match timeline) —
// ported logic-for-logic from js/renderers-matches.js. DOM-free so it can be
// unit-tested; MatchesModule (04k-b) paints what these return.
//
// The legacy chart is plain innerHTML rebuilt on every render (no d3), so the
// module renders these results as JSX — the ComparisonModule pattern, not a d3
// island. Everything here takes its data IN (appearances, events, matchGoals,
// matchResults) instead of reading window.CatalystData, so the Part-2 source
// cutover stays behind the hooks.

import type { ProtoAppearance, ProtoEvent, ProtoFixture, ProtoMatchGoal } from "@catbento/shared";
import { getPlayerShortName } from "../data/player-utils.js";
import { clockMinute } from "../data/match-minute.js";

// --- StatsBomb attribute narrowing --------------------------------------------
// ProtoEvent keeps raw_event_attributes loose (the whole StatsBomb schema would be
// noise). These are the only fields Matches reads. No defaults: the legacy reads
// them unguarded, so a missing field should throw here too (fail fast).

interface ShotAttrs {
  outcome: { name: string };
  statsbomb_xg?: number;
}
interface PassAttrs {
  shot_assist?: boolean;
  goal_assist?: boolean;
  recipient?: { name?: string } | null;
}
const shotAttrs = (e: ProtoEvent) => e.raw_event_attributes.shot as ShotAttrs;
const passAttrs = (e: ProtoEvent) => e.raw_event_attributes.pass as PassAttrs;

// --- Tooltips -------------------------------------------------------------------

export function shotTooltip(s: ProtoEvent): string {
  const info = shotAttrs(s);
  const xg = typeof info.statsbomb_xg === "number" ? info.statsbomb_xg.toFixed(2) : "—";
  return `${info.outcome.name} · ${clockMinute(Number(s.minute), s.period)}\nxG ${xg}`;
}

export function passTooltip(pa: ProtoEvent, label: string): string {
  const info = passAttrs(pa);
  const recipient = info.recipient && info.recipient.name ? ` to ${info.recipient.name}` : "";
  return `${label}${recipient} · ${clockMinute(Number(pa.minute), pa.period)}`;
}

// --- Incident types (the filter chips + their dots) -----------------------------

/** A match's goals as Team Goals reads them — the snapshot's `matchGoals` record fits, and so
 *  does the live one (T2-11), which knows a penalty but not the other shot types (null). */
export interface MatchGoalsView {
  homeTeam: string;
  goals: (Pick<ProtoMatchGoal, "team" | "teamName" | "minute" | "scorer"> & { shotType: string | null; period?: number | null })[];
}

/** Everything one appearance row's dot builders need. */
export interface IncidentContext {
  appearance: ProtoAppearance;
  shots: ProtoEvent[];
  passes: ProtoEvent[];
  playerTeam: string | null | undefined;
  /** window.CatalystData.matchGoals, passed in (the legacy reads it globally). */
  matchGoals: Record<string, MatchGoalsView>;
}

/** One dot on a row. `color` is optional — Team Goals needs two (for/against);
 *  otherwise the type's own swatch colour is used. */
export interface IncidentDot {
  minute: number;
  tooltip: string;
  color?: string;
}

export interface IncidentType {
  key: string;
  label: string;
  color: string;
  getDots: (ctx: IncidentContext) => IncidentDot[];
  /** Only on ?live=1 (the snapshot has no card / injury data); the demo keeps its chips. */
  liveOnly?: boolean;
}

/** The minute she went off, or null when she finished the match / never came on. */
function offMinuteOf(a: ProtoAppearance): number | null {
  return a.offPitchMinute === null ? null : Number(a.offPitchMinute);
}

// Order + colours VERBATIM (it's the chip order on screen). Key Passes and Assists
// are mutually exclusive on purpose (shot_assist splits into "led to a shot" vs
// "led to a goal"), so turning both on never stacks two dots on one pass.
export const INCIDENT_TYPES: readonly IncidentType[] = [
  {
    key: "shots",
    label: "Shots",
    color: "#cfd3da",
    getDots: (ctx) => ctx.shots.map((s) => ({ minute: Number(s.minute), tooltip: shotTooltip(s) })),
  },
  {
    key: "goals",
    label: "Goals",
    color: "#f5a623",
    getDots: (ctx) =>
      ctx.shots
        .filter((s) => shotAttrs(s).outcome.name === "Goal")
        .map((s) => ({ minute: Number(s.minute), tooltip: shotTooltip(s) })),
  },
  {
    key: "keyPasses",
    label: "Key Passes",
    color: "#4d8bf5",
    getDots: (ctx) =>
      ctx.passes
        .filter((pa) => passAttrs(pa).shot_assist && !passAttrs(pa).goal_assist)
        .map((pa) => ({ minute: Number(pa.minute), tooltip: passTooltip(pa, "Key Pass") })),
  },
  {
    key: "assists",
    label: "Assists",
    color: "#b06df2",
    getDots: (ctx) =>
      ctx.passes
        .filter((pa) => passAttrs(pa).goal_assist)
        .map((pa) => ({ minute: Number(pa.minute), tooltip: passTooltip(pa, "Goal Assist") })),
  },
  // Both sides' goals — game context (was the team already winning/losing), not
  // the player's own contribution. Green = for, red = against.
  {
    key: "teamGoals",
    label: "Team Goals",
    color: "#2ecc71",
    getDots: (ctx) => {
      const rec = ctx.matchGoals[String(ctx.appearance.matchId)];
      if (!rec) return [];
      const teamIsHome = rec.homeTeam === ctx.playerTeam;
      return rec.goals.map((g) => {
        const forUs = (g.team === "home") === teamIsHome;
        const pen = g.shotType === "Penalty" ? " (Pen)" : g.shotType === "Own Goal" ? " (OG)" : "";
        // The scorer is a plain string (an away scorer usually isn't in our player
        // db), so wrap it to reuse the first+last shortening.
        const scorer = getPlayerShortName({ name: g.scorer }) || g.scorer;
        return {
          minute: Number(g.minute),
          color: forUs ? "#2ecc71" : "#e74c3c",
          tooltip: `${scorer} (${g.teamName})${pen} · ${clockMinute(g.minute, g.period)}`,
        };
      });
    },
  },
  // A change point = onPitchMinute + the running sum of durations up to it (the
  // legacy checked this lands on offPitchMinute for every multi-position game).
  {
    key: "positionChanges",
    label: "Position Changes",
    color: "#00c2ff",
    getDots: (ctx) => {
      const positions = ctx.appearance.positions || [];
      if (positions.length < 2) return [];
      let cursor = Number(ctx.appearance.onPitchMinute) || 0;
      const dots: IncidentDot[] = [];
      for (let i = 0; i < positions.length - 1; i++) {
        cursor += Number(positions[i]!.duration) || 0;
        dots.push({ minute: cursor, tooltip: `Moved to ${positions[i + 1]!.position} · ${Math.round(cursor)}'` });
      }
      return dots;
    },
  },
  // Live (Ben, 2026-10-18 — Catalyst's Appearances chart): yellow cards at their minute; a red or a
  // second yellow at the minute she went off.
  {
    key: "cards",
    label: "Cards",
    color: "#eab308",
    liveOnly: true,
    getDots: (ctx) => {
      const dots: IncidentDot[] = (ctx.appearance.keyEvents ?? [])
        .filter((e) => e.type === "yellow_card")
        .map((e) => ({ minute: e.minute, tooltip: `Yellow card · ${clockMinute(e.minute)}` }));
      const off = offMinuteOf(ctx.appearance);
      const type = ctx.appearance.offPitchType;
      if (off !== null && type === "Red Card") dots.push({ minute: off, color: "#ef4444", tooltip: `Red card · ${clockMinute(off)}` });
      if (off !== null && type === "Second Yellow Card") dots.push({ minute: off, color: "#f97316", tooltip: `Second yellow · ${clockMinute(off)}` });
      return dots;
    },
  },
  {
    key: "injuries",
    label: "Injuries",
    color: "#7aa7ff",
    liveOnly: true,
    getDots: (ctx) => {
      const off = offMinuteOf(ctx.appearance);
      return off !== null && ctx.appearance.offPitchType === "Injury" ? [{ minute: off, tooltip: `Off injured · ${Math.floor(off)}'` }] : [];
    },
  },
];

/** A dot ready to paint: colour resolved + a stable per-row key. */
export interface RowDot {
  minute: number;
  tooltip: string;
  color: string;
  key: string;
}

/** The dots for row `rowIndex`, from every ACTIVE incident type, in chip order.
 *  Keys match the legacy `${type}${row}-${n}` (its data-dot-tip ids). */
export function rowDots(ctx: IncidentContext, filters: Record<string, boolean>, rowIndex: number): RowDot[] {
  const dots: RowDot[] = [];
  for (const t of INCIDENT_TYPES) {
    if (!filters[t.key]) continue;
    t.getDots(ctx).forEach((d, di) =>
      dots.push({ ...d, color: d.color || t.color, key: `${t.key}${rowIndex}-${di}` }),
    );
  }
  return dots;
}

// --- Opposition / result --------------------------------------------------------

/** "YYYY-MM-DD…" → "DD/MM/YY" (the row-label fallback when a match isn't in
 *  matchResults). */
export function formatRowDate(dateStr: string): string {
  const d = String(dateStr).slice(0, 10).split("-");
  if (d.length !== 3) return String(dateStr);
  return `${d[2]}/${d[1]}/${d[0]!.slice(2)}`;
}

export interface Opposition {
  opponent: string;
  isHome: boolean;
  homeScore: number;
  awayScore: number;
}

/** Find the match by numeric matchId across every matchResults competition (the
 *  competition labels don't string-match the appearances'). null = not captured —
 *  a real gap (e.g. UEFA Women's Champions League), not papered over. */
export function resolveOpposition(
  results: Record<string, ProtoFixture[]>,
  matchId: number,
  myTeam: string | null | undefined,
): Opposition | null {
  for (const compKey of Object.keys(results)) {
    const found = results[compKey]!.find((m) => m.matchId === matchId);
    if (found) {
      const isHome = myTeam === found.homeTeamName;
      return {
        opponent: isHome ? found.awayTeamName : found.homeTeamName,
        isHome,
        homeScore: found.homeScore,
        awayScore: found.awayScore,
      };
    }
  }
  return null;
}

export interface MatchResultInfo {
  opponent: string | null;
  isHome: boolean | null;
  /** "home-away" score, or "" when the match/score isn't captured. */
  text: string;
  myScore?: number;
  oppScore?: number;
}

export function matchResultInfo(
  results: Record<string, ProtoFixture[]>,
  matchId: number,
  myTeam: string | null | undefined,
): MatchResultInfo {
  const opp = resolveOpposition(results, matchId, myTeam);
  if (!opp || typeof opp.homeScore !== "number" || typeof opp.awayScore !== "number") {
    return { opponent: opp && opp.opponent, isHome: opp && opp.isHome, text: "" };
  }
  const myScore = opp.isHome ? opp.homeScore : opp.awayScore;
  const oppScore = opp.isHome ? opp.awayScore : opp.homeScore;
  return { opponent: opp.opponent, isHome: opp.isHome, text: `${opp.homeScore}-${opp.awayScore}`, myScore, oppScore };
}

/** Row label = opponent + H/A (not the date). Split so CSS can ellipsis a long
 *  team name without ever hiding the H/A. Falls back to the date (ha '') when the
 *  match isn't in matchResults. */
export function matchRowLabel(
  results: Record<string, ProtoFixture[]>,
  matchId: number,
  myTeam: string | null | undefined,
  matchDate: string,
): { name: string; ha: string } {
  const opp = resolveOpposition(results, matchId, myTeam);
  return opp ? { name: opp.opponent, ha: opp.isHome ? "H" : "A" } : { name: formatRowDate(matchDate), ha: "" };
}

/** The whole-row tooltip: opponent, score + W/L/D, minutes + how they came on/off. */
export function matchTooltip(
  results: Record<string, ProtoFixture[]>,
  a: ProtoAppearance,
  myTeam: string | null | undefined,
): string {
  const result = matchResultInfo(results, a.matchId, myTeam);
  const lines: string[] = [];
  lines.push(result.opponent ? `${result.opponent} (${result.isHome ? "H" : "A"})` : formatRowDate(a.matchDate));
  if (result.text) {
    const my = result.myScore!;
    const their = result.oppScore!;
    const cls = my > their ? "W" : my < their ? "L" : "D";
    lines.push(`${result.text} (${cls})`);
  }
  const onMin = Number(a.onPitchMinute) || 0;
  const offMin = a.offPitchMinute != null ? Number(a.offPitchMinute) : Number(a.minutesOnPitch);
  const minsPlayed = Math.round(Number(a.minutesOnPitch) || Math.max(0, offMin - onMin));
  // In the squad but never on (Ben, 2026-10-26): no minutes and no position — not "Started,
  // Played to full time", which is what the two checks below fall back to.
  if (minsPlayed === 0 && (a.positions || []).length === 0) {
    lines.push("Unused substitute");
    return lines.join("\n");
  }
  const startNote = onMin > 0.5 ? `On as sub (${Math.round(onMin)}')` : "Started";
  const endNote = a.offPitchType === "Substitution" ? `Off ${Math.round(offMin)}'` : "Played to full time";
  lines.push(`${minsPlayed} mins — ${startNote}, ${endNote}`);
  return lines.join("\n");
}

// --- Rows + axis geometry -------------------------------------------------------

/** Appearances only exist for the player's CURRENT season (the capture has no
 *  per-season split), so any other season is an honest empty list. Newest first. */
export function appearancesForSeason(
  appearances: ProtoAppearance[],
  seasonCode: string,
  currentSeason: string | undefined,
): ProtoAppearance[] {
  if (seasonCode !== currentSeason) return [];
  return appearances
    .slice()
    .sort((a, b) => new Date(b.matchDate).getTime() - new Date(a.matchDate).getTime());
}

const offPitchOf = (a: ProtoAppearance) => (a.offPitchMinute != null ? a.offPitchMinute : a.minutesOnPitch);

/** Axis end: the latest real off-pitch minute (added/extra time runs past 90),
 *  never below 90, rounded up to a clean multiple of 10 so ticks read evenly. */
export function matchesAxisMax(appearances: ProtoAppearance[]): number {
  return Math.ceil(Math.max(90, ...appearances.map((a) => Number(offPitchOf(a)) || 0)) / 10) * 10;
}

export interface AxisTick {
  value: number;
  leftPct: number;
  /** The end ticks anchor to their own edge so "0"/"110" don't overflow the chart. */
  align: "left" | "center" | "right";
}

export function matchesAxisTicks(axisMax: number): AxisTick[] {
  const ticks: AxisTick[] = [];
  for (let t = 0; t <= axisMax; t += 10) {
    ticks.push({
      value: t,
      leftPct: (t / axisMax) * 100,
      align: t === 0 ? "left" : t === axisMax ? "right" : "center",
    });
  }
  return ticks;
}

/** The on-pitch bar: starts at the real on-minute (a sub starts past 0), clipped
 *  to the axis. */
export function barGeometry(a: ProtoAppearance, axisMax: number): { startPct: number; widthPct: number } {
  const onMin = Number(a.onPitchMinute) || 0;
  const offMin = Math.min(
    axisMax,
    a.offPitchMinute != null ? Number(a.offPitchMinute) : Number(a.minutesOnPitch) || 0,
  );
  const startPct = (onMin / axisMax) * 100;
  const widthPct = Math.max(0, (offMin / axisMax) * 100 - startPct);
  return { startPct, widthPct };
}

/** A dot's left %, capped so an extra-time dot never overflows the track. */
export function dotLeftPct(minute: number, axisMax: number): number {
  return Math.min(100, (minute / axisMax) * 100);
}

/** Half-time line always; the full-time line only when the axis runs past 90. */
export function guideLines(axisMax: number): { halfPct: number; ftPct: number | null } {
  return { halfPct: (45 / axisMax) * 100, ftPct: axisMax > 90 ? (90 / axisMax) * 100 : null };
}

/** Where in a match she played each position: from–to minutes, in order (the durations add up from
 *  her on-pitch minute — the same walk as the "Position Changes" dots). Ben, 2026-10-18: click a
 *  position under the chart to see when and in which matches she played it. Pure. */
export function positionSegments(a: ProtoAppearance): { position: string; from: number; to: number }[] {
  let cursor = Number(a.onPitchMinute) || 0;
  return (a.positions || []).map((p) => {
    const from = cursor;
    cursor += Number(p.duration) || 0;
    return { position: p.position, from, to: cursor };
  });
}
